import { HttpHandler } from '@/common/http/HttpHandler';
import Endpoints from '@/common/http/Endpoints';
import IAdminService from './interfaces/IAdminService';
import {
  OnboardingRegistrationSummary,
  OnboardingRegistration,
  OnboardingRegistrationOdinAccount,
  TenantSummary,
  CreateNewOdinAccount,
  TenantDetailsApi,
  TenantUser,
  CloudOrganizationSummary,
  MicrosoftPartner,
  UserRoleAssignment,
  TenantUserInvitation,
  TenantPartner,
  AppConfiguration,
} from '@/common/models';


export class AdminService implements IAdminService {
  private httpHandler: HttpHandler;

  constructor(httpHandler: any) {
    this.httpHandler = httpHandler;
  }

  // Registrations
  getOnboardingRegistrations(): Promise<OnboardingRegistrationSummary[]> {
    return this.httpHandler.get<OnboardingRegistrationSummary[]>(Endpoints.AdminOnboardingRegistrations, {});
  }

  getOnboardingRegistration(registrationId: string): Promise<OnboardingRegistration> {
    return this.httpHandler.get<OnboardingRegistration>(Endpoints.AdminOnboardingRegistration(registrationId));
  }

  getOnboardingRegistrationOdinAccounts(registrationId: string): Promise<OnboardingRegistrationOdinAccount[]> {
    return this.httpHandler.get<OnboardingRegistrationOdinAccount[]>(Endpoints.AdminOnboardingRegistrationOdinAccounts(registrationId));
  }

  resetOnboardingRegistrationOdinSyncStatus(registrationId: string): Promise<void> {
    return this.httpHandler.post<void>(Endpoints.ResetOnboardingRegistrationOdinSyncStatus(registrationId), undefined);
  }

  updateOnboardingRegistration(onboardingRegistration: OnboardingRegistration): Promise<void> {
    return this.httpHandler.put<OnboardingRegistration>(Endpoints.AdminOnboardingRegistration(onboardingRegistration.registrationId), onboardingRegistration);
  }

  cancelOnboardingRegistration(registrationId: string): Promise<void> {
    return this.httpHandler.put<void>(Endpoints.AdminOnboardingRegistrationCancel(registrationId), undefined);
  }

  refreshOdinAccounts(registrationId: string): Promise<void> {
    return this.httpHandler.post<void>(Endpoints.AdminOnboardingRegistrationOdinAccounts(registrationId), undefined);
  }

  createOdinAccount(registrationId: string, odinAccount: CreateNewOdinAccount): Promise<void> {
    return this.httpHandler.post<CreateNewOdinAccount>(Endpoints.AdminOnboardingRegistrationCreateOdinAccount(registrationId), odinAccount);
  }

  // Tenants
  getTenants(): Promise<TenantSummary[]> {
    return this.httpHandler.get<TenantSummary[]>(Endpoints.AdminTenants, {});
  }

  getTenantDetails(tenantId: string): Promise<TenantDetailsApi> {
    return this.httpHandler.get<TenantDetailsApi>(Endpoints.AdminTenantDetails(tenantId));
  }

  getTenantPartners(tenantId: string): Promise<TenantPartner[]> {
    return this.httpHandler.get<TenantPartner[]>(Endpoints.AdminListPartners(tenantId));
  }

  updateTenantDetails(tenantId: string, tenantDetailsApi: TenantDetailsApi): Promise<void> {
    return this.httpHandler.put<TenantDetailsApi>(Endpoints.AdminTenantDetails(tenantId), tenantDetailsApi);
  }

  // Users
  getUsers(tenantId: string): Promise<TenantUser[]> {
    return this.httpHandler.get<TenantUser[]>(Endpoints.AdminTenantUsers(tenantId), {});
  }

  getTenantUser(tenantId: string, userId: string): Promise<TenantUser> {
    return this.httpHandler.get<TenantUser>(Endpoints.AdminTenantUser(tenantId, userId), {});
  }

  updateTenantUser(tenantId: string, tenantUser: TenantUser): Promise<void> {
    return this.httpHandler.put<TenantUser>(Endpoints.AdminTenantUser(tenantId, tenantUser.id), tenantUser);
  }

  deleteTenantUser(tenantId: string, tenantUser: TenantUser): Promise<void> {
    return this.httpHandler.delete(Endpoints.AdminTenantUser(tenantId, tenantUser.id));
  }

  inviteTenantUser(tenantId: string, tenantUser: TenantUser): Promise<void> {
    return this.httpHandler.post<TenantUser>(Endpoints.AdminTenantUserInvitations(tenantId), tenantUser);
  }

  selfInvite(tenantId: string): Promise<void> {
    return this.httpHandler.post(Endpoints.AdminTenantSelfInvite(tenantId), {});
  }

  getTenantUserInvitations(tenantId: string, userId: string): Promise<TenantUserInvitation[]> {
    return this.httpHandler.get<TenantUserInvitation[]>(Endpoints.GetTenantUserInvitations(tenantId, userId), {});
  }

  getUserRoleAssignments(tenantId: string, userId: string): Promise<UserRoleAssignment[]> {
    return this.httpHandler.get<UserRoleAssignment[]>(Endpoints.AdminTenantUserDetailRoleAssignments(tenantId, userId), {});
  }

  // Cloud Organizations
  getCloudOrganizations(): Promise<CloudOrganizationSummary[]> {
    return this.httpHandler.get<CloudOrganizationSummary[]>(Endpoints.AdminCloudOrganizations, {});
  }

  activateCloudOrganization(tenantId: string): Promise<void> {
    return this.httpHandler.put<void>(Endpoints.ActivateCloudOrganization(tenantId), undefined);
  }

  // Microsoft Partner
  getMicrosoftPartnerDetails(tenantId: string, mpnId: string): Promise<MicrosoftPartner> {
    return this.httpHandler.get<MicrosoftPartner>(Endpoints.MicrosoftPartnerDetails(tenantId, mpnId), undefined);
  }

  getConfigDetails(): Promise<AppConfiguration> {
    return this.httpHandler.get<AppConfiguration>(Endpoints.GetConfigurations);
  }
}
